/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {GetterTree} from 'vuex';
import {RootState} from '@/store/types';
import {CommunityChallengesStateInterface} from '@/store/modules/community-challenges/types';
import {CommunityChallengeInterface} from '@/store/modules/community-challenge/types';

/**
 * The community challenges module getters provide entry points for stored data.
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const getters: GetterTree<CommunityChallengesStateInterface, RootState> = {
    /**
     * Checks for loading state.
     * @param {CommunityChallengesStateInterface} state
     * @return {boolean}
     */
    loading(state: CommunityChallengesStateInterface): boolean {
        return state.loadStatus.loading;
    },

    /**
     * Checks for loading state failure.
     * @param {CommunityChallengesStateInterface} state
     * @return {boolean}
     */
    failed(state: CommunityChallengesStateInterface): boolean {
        return !state.loadStatus.loading && state.loadStatus.error;
    },

    /**
     * Checks for loading state success.
     * @param {CommunityChallengesStateInterface} state
     * @return {boolean}
     */
    loaded(state: CommunityChallengesStateInterface): boolean {
        return !state.loadStatus.loading && state.loadStatus.success;
    },

    /**
     * Checks to see if there is more results up-stream.
     * @param {CommunityChallengesStateInterface} state
     * @return {boolean}
     */
    hasNext(state: CommunityChallengesStateInterface): boolean {
        return undefined !== state.pagination && undefined !== state.pagination.next && !!state.pagination.next;
    },

    /**
     * Gets the local timestamp.
     * @param {CommunityChallengesStateInterface} state
     * @return {number}
     */
    timestamp(state: CommunityChallengesStateInterface): number {
        return state.timestamp;
    },

    /**
     * Returns the paginated total challenge count.
     * @param {CommunityChallengesStateInterface} state
     * @return {number}
     */
    challengeCount(state: CommunityChallengesStateInterface): number {
        return undefined !== state.pagination ? state.pagination.totalItems : 0;
    },

    /**
     * Counts the challenges.
     * @param {CommunityChallengesStateInterface} state
     * @return {number}
     */
    currentChallengeCount(state: CommunityChallengesStateInterface): number {
        return state.challenges.length;
    },

    /**
     * Gets the challenges.
     * @param {CommunityChallengesStateInterface} state
     * @return {CommunityChallengeInterface[]}
     */
    challenges(state: CommunityChallengesStateInterface): CommunityChallengeInterface[] {
        return state.challenges;
    },
};
